import axios, {
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:8080";

export interface ApiSuccess<T> {
  data: T;
  meta?: unknown;
  requestId?: string;
}

export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown };
  requestId?: string;
}

export class ApiError extends Error {
  code: string;
  status: number;
  details?: unknown;
  requestId?: string;

  constructor(params: {
    code: string;
    message: string;
    status: number;
    details?: unknown;
    requestId?: string;
  }) {
    super(params.message);
    this.code = params.code;
    this.status = params.status;
    this.details = params.details;
    this.requestId = params.requestId;
  }
}

// ---------------------------------------------------------------------------
// Token accessor — injected by auth store after it initialises, breaking the
// circular import that would happen if we imported the store here directly.
// ---------------------------------------------------------------------------
type TokenGetter = () => { accessToken: string | null; refreshToken: string | null };
type TokensSetter = (s: { accessToken: string; refreshToken: string }) => void;
type Clearer = () => void;
type StatusSetter = (s: "idle" | "authed" | "refreshing") => void;

let _getTokens: TokenGetter = () => ({ accessToken: null, refreshToken: null });
let _setTokens: TokensSetter = () => undefined;
let _clear: Clearer = () => undefined;
let _setStatus: StatusSetter = () => undefined;

export function registerAuthHooks(hooks: {
  getTokens: TokenGetter;
  setTokens: TokensSetter;
  clear: Clearer;
  setStatus: StatusSetter;
}) {
  _getTokens = hooks.getTokens;
  _setTokens = hooks.setTokens;
  _clear = hooks.clear;
  _setStatus = hooks.setStatus;
}

// ---------------------------------------------------------------------------
// Single-flight refresh
// ---------------------------------------------------------------------------
let isRefreshing = false;
let refreshQueue: Array<(token: string) => void> = [];

function drainQueue(token: string) {
  refreshQueue.forEach((cb) => cb(token));
  refreshQueue = [];
}

function abortQueue() {
  refreshQueue = [];
}

const AUTH_SKIP = ["/v1/auth/login", "/v1/auth/refresh"];

// ---------------------------------------------------------------------------
// Error normaliser
// ---------------------------------------------------------------------------
function toApiError(err: unknown): ApiError {
  if (err instanceof ApiError) return err;
  const e = err as {
    response?: { data?: ApiErrorBody & { code?: string; message?: string }; status?: number };
    message?: string;
  };
  const d = e.response?.data;
  if (d?.error) {
    return new ApiError({
      code: d.error.code,
      message: d.error.message,
      status: e.response?.status ?? 0,
      details: d.error.details,
    });
  }
  // Some middleware (e.g. the must_change_password gate) returns a flat
  // { code, message } body rather than the nested { error: {...} } envelope.
  if (d?.code) {
    return new ApiError({
      code: d.code,
      message: d.message ?? d.code,
      status: e.response?.status ?? 0,
    });
  }
  return new ApiError({
    code: "network_error",
    message: e.message ?? "network error",
    status: e.response?.status ?? 0,
  });
}

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------
function createClient(): AxiosInstance {
  const client = axios.create({
    baseURL: API_BASE,
    timeout: 30_000,
    headers: { "Content-Type": "application/json" },
  });

  // Attach bearer token
  client.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const { accessToken } = _getTokens();
    if (accessToken && config.headers) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }
    return config;
  });

  // 401 → single-flight refresh
  client.interceptors.response.use(
    (res: AxiosResponse<ApiSuccess<unknown>>) => res,
    async (err: unknown) => {
      const axiosErr = err as {
        response?: { status?: number; data?: ApiErrorBody & { code?: string } };
        config?: InternalAxiosRequestConfig & { _retry?: boolean };
      };

      const cfg = axiosErr.config;
      const status = axiosErr.response?.status;
      // The must_change_password gate returns a flat { code } body; the standard
      // envelope nests it under error. Read both.
      const code = axiosErr.response?.data?.error?.code ?? axiosErr.response?.data?.code;
      const isSkip = AUTH_SKIP.some((u) => cfg?.url?.includes(u));

      // Password rotation required — bounce to /login so the change-password
      // dialog runs. Without this the flat 403 surfaces as a confusing network error.
      if (status === 403 && code === "must_change_password") {
        window.location.replace("/login");
        _clear();
        return Promise.reject(toApiError(err));
      }

      if (status === 401 && code === "unauthorized" && !isSkip && cfg && !cfg._retry) {
        cfg._retry = true;
        const { refreshToken } = _getTokens();

        if (!refreshToken) {
          // Replace before clear so the in-flight React render doesn't see the
          // logged-out store and trigger Navigate → infinite update loop.
          window.location.replace("/login");
          _clear();
          return Promise.reject(toApiError(err));
        }

        if (isRefreshing) {
          return new Promise<AxiosResponse>((resolve) => {
            refreshQueue.push((newToken) => {
              if (cfg.headers) cfg.headers["Authorization"] = `Bearer ${newToken}`;
              resolve(client(cfg));
            });
          });
        }

        isRefreshing = true;
        _setStatus("refreshing");

        try {
          type RefreshPayload = {
            accessToken: string;
            refreshToken: string;
          };
          const res = await axios.post<ApiSuccess<RefreshPayload>>(
            `${API_BASE}/v1/auth/refresh`,
            { refreshToken },
            { headers: { "Content-Type": "application/json" } },
          );

          // Identity unchanged on refresh — only rotate tokens, keep existing
          // user in store. Overwriting with undefined would trip RequireAuth
          // and force a redirect to /login despite a successful refresh.
          const { accessToken: newAccess, refreshToken: newRefresh } = res.data.data;
          _setTokens({ accessToken: newAccess, refreshToken: newRefresh });
          drainQueue(newAccess);

          if (cfg.headers) cfg.headers["Authorization"] = `Bearer ${newAccess}`;
          return client(cfg);
        } catch {
          abortQueue();
          window.location.replace("/login");
          _clear();
          return Promise.reject(toApiError(err));
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(toApiError(err));
    },
  );

  return client;
}

export const apiClient = createClient();

export async function apiGet<T>(url: string, params?: unknown): Promise<T> {
  const res = await apiClient.get<ApiSuccess<T>>(url, { params });
  return res.data.data;
}

export async function apiPost<T, B = unknown>(url: string, body?: B): Promise<T> {
  const res = await apiClient.post<ApiSuccess<T>>(url, body);
  return res.data.data;
}

export async function apiPatch<T, B = unknown>(url: string, body?: B): Promise<T> {
  const res = await apiClient.patch<ApiSuccess<T>>(url, body);
  return res.data.data;
}

export async function apiPut<T, B = unknown>(url: string, body?: B): Promise<T> {
  const res = await apiClient.put<ApiSuccess<T>>(url, body);
  return res.data.data;
}

export async function apiDelete(url: string): Promise<void> {
  await apiClient.delete(url);
}
